// src/registered/site-admin/components/Workflow-esg/controllers/XbrlProcessorController.jsx
import React, { useState } from "react";
import XbrlProcessorView from "../views/XbrlProcessorView";
import { ingestTaxonomy } from "../services/SustinaXbrlProcessorService";
import { syncIngestedTaxonomyToSql, deriveTaxonomyVersion } from "../services/EsgTaxonomySyncService";

export default function XbrlProcessorController({ infraStatus }) {
  const [entryPointPath, setEntryPointPath] = useState("");
  const [isIngesting, setIsIngesting] = useState(false);
  const [ingestResult, setIngestResult] = useState(null);
  const [ingestError, setIngestError] = useState(null);
  const [syncStatus, setSyncStatus] = useState("");

  const siteKey = process.env.REACT_APP_SITE_KEY;

  const handleIngestTaxonomy = async () => {
    setIsIngesting(true);
    setIngestError(null);
    setIngestResult(null);

    try {
      const response = await ingestTaxonomy({ entryPointPath });

      if (!response?.success) {
        // executeWorkflowHandshake never throws on a failed call - a
        // {success:false, ...} response comes back normally, so it has to be
        // checked explicitly rather than assumed to mean success.
        console.error("🚨 [XBRL_INGEST_FAILED]:", response);

        // response.data may hold the raw Lambda error JSON as a string
        // (via the BFF's "details" wrapper) - try to pull the real reason out of it.
        let detailMessage = null;
        if (typeof response?.data === "string") {
          try {
            detailMessage = JSON.parse(response.data)?.message;
          } catch {
            // not JSON - ignore, fall back below
          }
        }

        setIngestError(detailMessage || response?.message || "Ingest failed. See console for details.");
        return;
      }

      const data = response.data ?? response;
      setIngestResult(data);

      // 🎯 Fold the SQL sync into the same ingest action - the catalogue is
      // useless for company-taxonomy binding/supplier classification until
      // Ref.ESG_TaxonomyDetails has it, so there's no separate "sync" step
      // to remember to click. A sync failure doesn't undo the S3 ingest
      // (which already succeeded) - it's surfaced but non-fatal.
      try {
        const sync = await syncIngestedTaxonomyToSql(
          {
            bucket: data.bucket,
            key: data.key,
            ...deriveTaxonomyVersion(entryPointPath),
            onProgress: setSyncStatus,
          },
          { siteKey }
        );
        setIngestResult({
          ...data,
          headerId: sync.headerId,
          syncedConceptCount: sync.conceptCount,
          syncedRoleCount: sync.roleCount,
          syncedArcCount: sync.arcCount,
          unresolvedArcCount: sync.unresolvedArcCount,
          hasHierarchy: sync.hasHierarchy,
        });
      } catch (syncErr) {
        console.error("🚨 [XBRL_SQL_SYNC_ERROR]:", syncErr.message);
        setIngestError(`Ingested to S3, but SQL sync failed: ${syncErr.message}`);
      }
    } catch (err) {
      console.error("🚨 [XBRL_INGEST_ERROR]:", err.message);
      setIngestError(err.message);
    } finally {
      setIsIngesting(false);
      setSyncStatus("");
    }
  };

  return (
    <XbrlProcessorView
      infraStatus={infraStatus}
      entryPointPath={entryPointPath}
      setEntryPointPath={setEntryPointPath}
      isIngesting={isIngesting}
      ingestResult={ingestResult}
      ingestError={ingestError}
      syncStatus={syncStatus}
      onIngestTaxonomy={handleIngestTaxonomy}
    />
  );
}
